import { FormSectionHandler } from '../entity.handler';
import { UserResponse, UserUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, ClientSelectField, ToggleField, CostCentreField } from '../utils';
import { ICONS } from '../icons';
import { AssetGroupingService } from 'app/services';
import { getPasswordRequirementText } from 'app/shared/utils/passwords';

interface EditorValues {
    name?: string;
    emailAddress?: string;
    mobile?: string;
    timeZoneId?: string;
    language?: string;
    defaultClient?: IdName;
    costCentre?: IdName;
    password?: string;
    invite?: boolean;
}

export class UserDetailsSectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = ICONS.user;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        private grouping: AssetGroupingService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['name', 'emailAddress', 'mobile', 'timeZoneId', 'language', 'defaultClient', 'costCentre', 'password'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.DETAILS.${key}`, values);
    }

    applyUpdate(entity: UserResponse, payload: UserUpdateRequest): Promise<UserResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateUser(entity.id, payload));
    }

    async mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        if (this.creating) {
            const company = await this.admin.getCached(`getCompany/${this.ownerId}`, api => api.accounts.getCompany(this.ownerId));
            const result: EditorValues = {
                timeZoneId: company.timeZoneId || this.admin.user.timeZoneId || 'GMT+0',
                language: company.language || this.admin.user.language || 'en-us',
                invite: true,
            };
            if (company.type === 'client') {
                const costCentres = await this.grouping.getCostCentres(company.id);
                const costCentre = costCentres.filter(x => x.parent === 'root')[0] || costCentres[0];
                result.costCentre = costCentre && { id: costCentre.id, name: costCentre.name };
            }
            return result;
        } else {
            return EntityUtils.mergeFields(this.properties, entity, {});
        }
    }

    mapEditorToEntity(entity: UserUpdateRequest, values: EditorValues): Promise<UserUpdateRequest> {
        if (this.creating) {
            values.name = values.name || values.emailAddress.split('@')[0]; // name is optional when creating, assume email address
            if (values.invite) {
                entity['inviteUrl'] = 'https://{domain}/#/login?token={token}'; // TODO: fix typings when fleet-api-client is updated
            }
        }
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    getFields(entity: UserResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.admin.getCached(`getCompany/${this.ownerId}`, api => api.accounts.getCompany(this.ownerId)),
            this.admin.getTimeZones(),
            this.admin.getLanguages(),
            this.grouping && this.grouping.getCostCentres(this.ownerId),
        ]).then(([company, timeZones, languages, costCentres]) => {
            const passwordComplexity = getPasswordRequirementText(this.admin.i18n, company);
            return [
                TextField('name', this.translate('FIELDS.NAME'), !this.creating, 1, 255),
                TextField('emailAddress', this.translate('FIELDS.EMAIL_ADDRESS'), true, 1, 255),
                TextField('mobile', this.translate('FIELDS.MOBILE_NUMBER'), false, 1, 255),
                this.creating ? null : { ...TextField('password', this.translate('FIELDS.PASSWORD'), this.creating, 1, 255), type: 'password', description: passwordComplexity.map(x => `* ${x}`).join('\r\n') },
                costCentres && company.type === 'client' ? CostCentreField(this.ownerId, 'costCentre', this.translate('FIELDS.COST_CENTRE'), true) : null,
                ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), true, timeZones.map(x => ({ key: x.id, value: x.name }))),
                ComboField('language', this.translate('FIELDS.LANGUAGE'), true, languages.map(x => ({ key: x.id, value: x.name }))),
                company.type !== 'client' ? ClientSelectField('defaultClient', this.translate('FIELDS.DEFAULT_CLIENT'), true, this.ownerId) : null,
                this.creating ? { ...ToggleField('invite', this.translate('FIELDS.SEND_INVITE'), true), description: this.translate('FIELDS.SEND_INVITE_DESC') } : null,
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, ['timeZoneId', 'language', 'defaultClient', 'costCentre'], fields));
    }

}

